'use client'

import { type FC, useCallback, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { LoadingButton } from 'base-ui'

import {
  FEATURE_TOGGLE_SELLER_CENTRAL,
  useFeatureToggle,
} from '../../feature-toggles'
import { FriendlyCaptchaChallenge } from '../../friendly-captcha'
import { useMarketplacePartnerRegisterPageSendEmail } from '../hooks/useMarketplacePartnerRegisterPageSendEmail'
import { useMarketplacePartnerRegisterPageSendEmailV3 } from '../hooks/useMarketplacePartnerRegisterPageSendEmailV3'

import { useMarketplacePartnerRegisterPageContext } from './marketplacePartnerRegisterPageContext'
import { MarketplacePartnerRegisterPageFormBasicInformations } from './MarketplacePartnerRegisterPageForm/MarketplacePartnerRegisterPageFormBasicInformations'
import { MarketplacePartnerRegisterPageFormBrands } from './MarketplacePartnerRegisterPageForm/MarketplacePartnerRegisterPageFormBrands'
import { MarketplacePartnerRegisterPageFormCategories } from './MarketplacePartnerRegisterPageForm/MarketplacePartnerRegisterPageFormCategories'
import { MarketplacePartnerRegisterPageFormCustomMessage } from './MarketplacePartnerRegisterPageForm/MarketplacePartnerRegisterPageFormCustomMessage/MarketplacePartnerRegisterPageFormCustomMessage'
import { MarketplacePartnerRegisterPageFormMarketing } from './MarketplacePartnerRegisterPageForm/MarketplacePartnerRegisterPageFormMarketing'
import { MarketplacePartnerRegisterPageFormMessages } from './MarketplacePartnerRegisterPageForm/MarketplacePartnerRegisterPageFormMessages'
import { MarketplacePartnerRegisterPageFormTechnicalInformations } from './MarketplacePartnerRegisterPageForm/MarketplacePartnerRegisterPageFormTechnicalInformations'
import { MarketplacePartnerRegisterPageFormV3 } from './MarketplacePartnerRegisterPageForm/MarketplacePartnerRegisterPageFormV3'

export const MarketplacePartnerRegisterPageContent: FC = () => {
  const { formRef, formState } = useMarketplacePartnerRegisterPageContext()
  const isSellerCentralFeatureEnabled = useFeatureToggle(
    FEATURE_TOGGLE_SELLER_CENTRAL,
  )
  const [isFriendlyCaptchaDone, setIsFriendlyCaptchaDone] =
    useState<boolean>(false)
  const {
    errors: errorsV1,
    handleSendEmail: handleSendEmailV1,
    isMutating: isMutatingV1,
  } = useMarketplacePartnerRegisterPageSendEmail()
  const {
    errors: errorsV3,
    form: v3Form,
    handleSendEmail: handleSendEmailV3,
    isMutating: isMutatingV3,
    setCaptchaSolution,
  } = useMarketplacePartnerRegisterPageSendEmailV3()

  const errors = isSellerCentralFeatureEnabled ? errorsV3 : errorsV1
  const isMutating = isSellerCentralFeatureEnabled ? isMutatingV3 : isMutatingV1

  const captchaDoneCallback = useCallback(
    (fcSolution: string): void => {
      if (isSellerCentralFeatureEnabled) {
        setCaptchaSolution(fcSolution)
      } else {
        formState.setValue(fcSolution, formState.model, 'fcSolution')
      }
      setIsFriendlyCaptchaDone(true)
    },
    [formState, isSellerCentralFeatureEnabled, setCaptchaSolution],
  )

  return (
    <form
      className="container mt-8 flex flex-col gap-4"
      data-qa-id="marketplace-partner-register-page-form"
      onSubmit={(event) => {
        if (isSellerCentralFeatureEnabled) {
          void handleSendEmailV3(event)
        } else {
          void handleSendEmailV1(event)
        }
      }}
      ref={formRef}
    >
      <div className="flex flex-col gap-20">
        {isSellerCentralFeatureEnabled ? (
          <MarketplacePartnerRegisterPageFormV3 form={v3Form} />
        ) : (
          <>
            <MarketplacePartnerRegisterPageFormBasicInformations />
            <MarketplacePartnerRegisterPageFormCategories />
            <MarketplacePartnerRegisterPageFormBrands />
            <MarketplacePartnerRegisterPageFormTechnicalInformations />
            <MarketplacePartnerRegisterPageFormMarketing />
            <MarketplacePartnerRegisterPageFormCustomMessage />
          </>
        )}
      </div>
      <FriendlyCaptchaChallenge
        className="mb-3.5"
        doneCallback={captchaDoneCallback}
        startMode="auto"
        whenThisPropChangesResetCaptcha={errors}
      />
      <MarketplacePartnerRegisterPageFormMessages errors={Boolean(errors)} />
      <LoadingButton
        data-qa-id="sellerForm-button-submit"
        disabled={!isFriendlyCaptchaDone}
        loading={isMutating}
        type="submit"
      >
        <FormattedMessage id="marketplacePartnerRegisterPage.button.submit" />
      </LoadingButton>
      <div className="text-center">
        <FormattedMessage id="marketplacePartnerRegisterPage.termsAndConditions.title" />
      </div>
    </form>
  )
}
